import { ActionPayloadGeneric } from "@app/root-store/actions";
import {
    createActionSet,
    createCrudActionSets,
    createDeletePromptActionSet,
    createNoPropsAction,
    createSuccessAction,
} from "@app/root-store/actions/action-creators";
import { ClareDbCollection } from "@app/shared/resources/resource-objectifier";
import { DataServiceError } from "@app/shared/services/data-service-error";
import { TypedAction } from "@ngrx/store/src/models";
import { User } from "../../model/user";

type ModelType = User;
export type UserAction = ActionPayload & TypedAction<string>;
export type UserSuccessAction = ActionResult & TypedAction<string>;
export type UserFailureAction = DataServiceError<ActionPayload> & TypedAction<string>;

export type UserActions = UserAction | UserSuccessAction | UserFailureAction;

/**
 * A payload used when performing an operation on a single entity
 */
export type ActionPayload = ActionPayloadGeneric & {
    user: ModelType;
};

/**
 * A payload used when loading an entity
 */
export interface LoadActionPayload {
    userId: number;
}

/**
 * An action result that returns a collection of entities
 */
export interface BulkActionResult {
    users: ClareDbCollection<ModelType>;
}

/**
 * An action result that returns a single entity
 */
export interface ActionResult {
    user: ModelType;
}

interface LoadEntityUsersPayload {
    entityId: number;
}

interface WelcomeUserEmailPayload {
    recipientEmail: string;
    recipientFirstName: string;
    recipientLastName: string;
    inviteCode: string;
}

/**
 * @description Used in the action creators to generate an action type like:
 * "[Entity Management] Load Entity"
 */
const actionCategory = "User";

/**
 * Create the CRUD set of actions associated with the action category.
 * Must send in appropriate payload types for a single and load and result types for single and bulk.
 * A request, success and failure action are created for each CRUD operation.
 * Each action set should be deconstructed into consts from the result.
 */
export const {
    load: loadSet,
    loadSingle: loadSingleSet,
    create: createSet,
    update: updateSet,
    delete: deleteSet,
} = createCrudActionSets<ActionPayload, LoadActionPayload, ActionResult, BulkActionResult>(actionCategory);

/**
 * Create each action from the deconstructed action sets created by createCrudActionSets.
 * Each action is strongly typed and should be used in accordance with the NgRx docs.
 * Any action is optional and can be omitted by simply not desctructuring it.
 */
export const { request: load, success: loadSuccess, failure: loadFailure } = loadSet;
export const { request: get, success: getSuccess, failure: getFailure } = loadSingleSet;
export const { request: create, success: createSuccess, failure: createFailure } = createSet;
export const { request: update, success: updateSuccess, failure: updateFailure } = updateSet;
export const { request: deleteItem, success: deleteSuccess, failure: deleteFailure } = deleteSet;

/**
 * An action set to prompt a user when deleting an item
 */
export const { confirm: deleteConfirmation, dismiss: deleteConfirmationDismiss } =
    createDeletePromptActionSet<ActionPayload>(actionCategory);

export const deleteConfirmationSet = { ...deleteSet, deleteConfirmation, deleteConfirmationDismiss };

export const sendUserWelcomeEmailSet = createActionSet<WelcomeUserEmailPayload, WelcomeUserEmailPayload>("User Welcome Email", "Send");
export const {
    request: sendUserWelcomeEmail,
    success: sendUserWelcomeEmailSuccess,
    failure: sendUserWelcomeEmailFailure,
} = sendUserWelcomeEmailSet;

export const loadAllEntityUsersSet = createActionSet<{ entityId: number }, BulkActionResult>("All Entity Users", "Load");
export const {
    request: loadAllEntityUsers,
    success: loadAllEntityUsersSuccess,
    failure: loadAllEntityUsersFailure,
} = loadAllEntityUsersSet;

export const loadAllEntityCustomersSet = createActionSet<{ entityId: number }, BulkActionResult>("All Entity Customers", "Load");
export const {
    request: loadAllEntityCustomers,
    success: loadAllEntityCustomersSuccess,
    failure: loadAllEntityCustomersFailure,
} = loadAllEntityCustomersSet;

export const loadEntityUsersSet = createActionSet<LoadEntityUsersPayload, { users: ModelType[] }>("Entity Users", "Load");
export const { request: loadEntityUsers, success: loadEntityUsersSuccess, failure: loadEntityUsersFailure } = loadEntityUsersSet;

export const loadActiveProjectUserSet = createActionSet<{ userId: number }, ActionResult>("Active Project User", "Load");
export const {
    request: loadActiveProjectUser,
    success: loadActiveProjectUserSuccess,
    failure: loadActiveProjectUserFailure,
} = loadActiveProjectUserSet;

export const clearActiveProjectUser = createNoPropsAction("Active Project User", "Clear");

export const clearActiveEntityStaff = createNoPropsAction("Entity Users", "Clear");

export const upsertSuccessSilent = createSuccessAction<ActionResult>("User Silent", "Upsert");

export const loadUserIdSuccess = createSuccessAction<ActionResult>("User ID Silent", "Load");
